import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  FiActivity,
  FiAlertCircle,
  FiAlertTriangle,
  FiArrowUpRight,
  FiCalendar,
  FiCamera,
  FiCheckCircle,
  FiChevronRight,
  FiClock,
  FiDatabase,
  FiGrid,
  FiImage,
  FiLayers,
  FiRefreshCw,
  FiShoppingBag,
  FiUploadCloud,
  FiUsers,
} from "react-icons/fi";
import { Link } from "react-router-dom";
import MenuLateral from "../../Components/MenuLateral";
import { LoginContext } from "../../context/LoginContext";
import api from "../../services/api";
import { APP_ROUTES, getDashboardDefinition } from "../../auth/access";
import LocalStorageCapacity from "../../Components/LocalStorageCapacity";
import { getRecognitionHealthPresentation } from "../../services/recognitionStatus";
import "./index.css";

const EMPTY_SUMMARY = {
  institutions: 0,
  classes: 0,
  students: 0,
  events: 0,
  photos: 0,
};

const KPI_PRESENTATIONS = {
  institutions: { icon: FiGrid, tone: "blue", detail: "Cadastros ativos" },
  classes: { icon: FiLayers, tone: "violet", detail: "Estrutura acadêmica" },
  students: { icon: FiUsers, tone: "teal", detail: "Pessoas na base" },
  events: { icon: FiCalendar, tone: "amber", detail: "Eventos em operação" },
  photos: { icon: FiImage, tone: "coral", detail: "Acervo operacional" },
};

const ROLE_CONTENT = {
  ADMIN: {
    eyebrow: "Painel operacional",
    intro: "",
    overviewLabel: "Panorama da empresa",
    overviewDescription: "Os números principais da operação ativa.",
    actions: [
      {
        label: "Abrir eventos",
        description: "Acompanhe a agenda e os vínculos.",
        path: APP_ROUTES.EVENTS,
        icon: FiCalendar,
      },
      {
        label: "Enviar fotos",
        description: "Continue um lote de trabalho.",
        path: APP_ROUTES.UPLOAD,
        icon: FiUploadCloud,
      },
      {
        label: "Revisar publicação de fotos",
        description: "Veja a fila que pede decisão.",
        path: APP_ROUTES.PUBLICATION_OPERATIONS,
        icon: FiCamera,
      },
      {
        label: "Registrar venda",
        description: "Crie um novo pedido comercial.",
        path: APP_ROUTES.SALES,
        icon: FiShoppingBag,
      },
    ],
  },
  PHOTOGRAPHER: {
    eyebrow: "Sua operação",
    intro: "Acompanhe somente os eventos e fotos vinculados ao seu perfil.",
    overviewLabel: "Seu recorte de trabalho",
    overviewDescription: "O que está sob sua responsabilidade agora.",
    actions: [
      {
        label: "Ver eventos",
        description: "Confira os eventos vinculados.",
        path: APP_ROUTES.EVENTS,
        icon: FiCalendar,
      },
      {
        label: "Enviar fotos",
        description: "Adicione um novo lote ao evento.",
        path: APP_ROUTES.UPLOAD,
        icon: FiUploadCloud,
      },
    ],
  },
};

const formatMetric = (value) => {
  if (value === "-" || value === null || value === undefined) return "-";

  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString("pt-BR")
    : "0";
};

function getApiMessage(error, fallback) {
  const apiMessage = error?.response?.data?.message;
  if (Array.isArray(apiMessage)) return apiMessage.join(", ");
  return apiMessage || fallback;
}

function Menu() {
  const { LoggedUser } = useContext(LoginContext);
  const role = LoggedUser?.role;
  const dashboard = getDashboardDefinition(role);
  const content = ROLE_CONTENT[role] || ROLE_CONTENT.ADMIN;

  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [loading, setLoading] = useState(true);
  const [summaryError, setSummaryError] = useState("");
  const [localStorageCapacity, setLocalStorageCapacity] = useState(null);
  const [localStorageLoading, setLocalStorageLoading] = useState(false);
  const [localStorageError, setLocalStorageError] = useState("");
  const [recognitionOperations, setRecognitionOperations] = useState(null);
  const [recognitionLoading, setRecognitionLoading] = useState(false);
  const [recognitionError, setRecognitionError] = useState("");

  useEffect(() => {
    let active = true;

    const loadDashboard = async () => {
      setLoading(true);
      setSummaryError("");

      try {
        const response = await api.get("/dashboard/summary");

        if (!active) return;

        setSummary(response.data?.summary || EMPTY_SUMMARY);
      } catch (error) {
        if (!active) return;

        setSummary(EMPTY_SUMMARY);
        setSummaryError(getApiMessage(error, "Não foi possível carregar o dashboard."));
      } finally {
        if (active) setLoading(false);
      }
    };

    loadDashboard();

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (role !== "ADMIN") {
      setLocalStorageCapacity(null);
      setLocalStorageLoading(false);
      setLocalStorageError("");
      return undefined;
    }

    let active = true;

    const loadLocalStorageCapacity = async () => {
      setLocalStorageLoading(true);
      setLocalStorageError("");

      try {
        const response = await api.get("/image-processing/policy");

        if (!active) return;

        setLocalStorageCapacity(response.data?.localStorageCapacity || null);
      } catch (error) {
        if (!active) return;

        setLocalStorageCapacity(null);
        setLocalStorageError(
          getApiMessage(error, "Não foi possível atualizar a capacidade local."),
        );
      } finally {
        if (active) setLocalStorageLoading(false);
      }
    };

    loadLocalStorageCapacity();

    return () => {
      active = false;
    };
  }, [role]);

  useEffect(() => {
    if (role !== "ADMIN") {
      setRecognitionOperations(null);
      setRecognitionLoading(false);
      setRecognitionError("");
      return undefined;
    }

    let active = true;

    const loadRecognitionOperations = async () => {
      setRecognitionLoading(true);
      setRecognitionError("");

      try {
        const response = await api.get("/recognition/operations/summary");
        if (active) setRecognitionOperations(response.data || null);
      } catch (error) {
        if (!active) return;

        setRecognitionOperations(null);
        setRecognitionError(
          getApiMessage(
            error,
            "Não foi possível atualizar o quadro de análise facial.",
          ),
        );
      } finally {
        if (active) setRecognitionLoading(false);
      }
    };

    loadRecognitionOperations();

    return () => {
      active = false;
    };
  }, [role]);

  const cards = useMemo(
    () =>
      (dashboard?.cards || []).map((card) => ({
        ...card,
        value: summary[card.field] ?? 0,
        ...(KPI_PRESENTATIONS[card.field] || {}),
      })),
    [dashboard, summary],
  );

  const operationCards = useMemo(() => {
    const operations = summary.operations || {};
    return [
      {
        title: "Eventos com reconhecimento pendente",
        value: operations.eventsRecognitionPending || 0,
        tone: "warning",
        icon: FiClock,
        detail: "Aguardam a conclusão do reconhecimento.",
      },
      {
        title: "Fotos com falha",
        value: operations.failedPhotos || 0,
        tone: "danger",
        icon: FiAlertCircle,
        detail: "Precisam de avaliação administrativa.",
      },
      {
        title: "Fotos desconhecidas",
        value: operations.unknownPhotos || 0,
        tone: "warning",
        icon: FiAlertTriangle,
        detail: "Podem exigir uma decisão de classificação.",
      },
    ];
  }, [summary]);

  const monthlyCards = useMemo(() => {
    const monthly = summary.monthly || {};
    return [
      { title: "Pedidos registrados no mês", value: monthly.ordersCreated || 0, icon: FiShoppingBag },
      { title: "Total de pedidos", value: monthly.totalOrders || 0, icon: FiLayers },
      { title: "Eventos no mês", value: monthly.events || 0, icon: FiCalendar },
      { title: "Fotos enviadas no mês", value: monthly.photosUploaded || 0, icon: FiUploadCloud },
      { title: "Alunos cadastrados no mês", value: monthly.studentsCreated || 0, icon: FiUsers },
    ];
  }, [summary]);

  return (
    <div className="pagina-dashboard">
      <MenuLateral />

      <main className="conteudo-dashboard" aria-busy={loading}>
        <div className="dashboard-inner">
          <header className="dashboard-header">
            <div className="dashboard-heading">
              <span className="dashboard-eyebrow">{content.eyebrow}</span>
              <h1>{dashboard?.title}</h1>
              <p>{content.intro}</p>
            </div>

            <div className={`dashboard-sync-status${loading ? " is-loading" : ""}`} role="status">
              <span className="dashboard-sync-dot" aria-hidden="true" />
              <span>{loading ? "Atualizando indicadores" : "Dados sincronizados"}</span>
              {loading ? <FiRefreshCw aria-hidden="true" /> : <FiCheckCircle aria-hidden="true" />}
            </div>
          </header>

          {summaryError ? (
            <div className="dashboard-banner error" role="alert">
              <FiAlertCircle aria-hidden="true" />
              <span>{summaryError}</span>
            </div>
          ) : null}

          <section className="dashboard-overview" aria-labelledby="dashboard-overview-title">
            <div className="dashboard-section-heading">
              <div>
                <span className="dashboard-section-kicker">Leitura principal</span>
                <h2 id="dashboard-overview-title">{content.overviewLabel}</h2>
                <p>{content.overviewDescription}</p>
              </div>
              <span className="dashboard-section-count">
                {cards.length} indicadores principais
              </span>
            </div>

            <div className="cards-dashboard">
              {cards.map((card) => (
                <DashboardCard
                  key={card.title}
                  title={card.title}
                  value={card.value}
                  detail={card.detail}
                  tone={card.tone}
                  Icon={card.icon}
                />
              ))}
            </div>
          </section>

          {dashboard?.showOperationalIndicators ? (
            <div className="dashboard-workspace">
              <OperationalFocusPanel cards={operationCards} />
              <RecognitionDashboardPanel
                loading={recognitionLoading}
                error={recognitionError}
                operations={recognitionOperations}
              />
            </div>
          ) : null}

          {dashboard?.showMonthlyIndicators ? (
            <section className="dashboard-panel dashboard-monthly-panel" aria-labelledby="dashboard-monthly-title">
              <div className="dashboard-panel-header">
                <PanelHeading
                  Icon={FiActivity}
                  kicker="Ritmo da operação"
                  title="Indicadores mensais"
                  description=""
                  titleId="dashboard-monthly-title"
                />
                <span className="dashboard-panel-period">Mês atual</span>
              </div>

              <div className="dashboard-monthly-grid">
                {monthlyCards.map((card) => (
                  <DashboardIndicatorCard
                    key={card.title}
                    title={card.title}
                    value={card.value}
                    Icon={card.icon}
                    tone="neutral"
                  />
                ))}
              </div>
            </section>
          ) : null}

          {role === "ADMIN" && localStorageLoading ? (
            <div className="dashboard-inline-state" role="status">
              <FiRefreshCw aria-hidden="true" />
              <span>Atualizando capacidade local...</span>
            </div>
          ) : null}

          {role === "ADMIN" && localStorageError ? (
            <div className="dashboard-banner error" role="alert">
              <FiAlertCircle aria-hidden="true" />
              <span>{localStorageError}</span>
            </div>
          ) : null}

          <div className={role === "ADMIN" && localStorageCapacity ? "dashboard-bottom-grid" : "dashboard-single-bottom"}>
            {role === "ADMIN" ? (
              <LocalStorageCapacity
                capacity={localStorageCapacity}
                className="dashboard-local-storage-capacity"
              />
            ) : null}
            <DashboardQuickActions actions={content.actions} />
          </div>

          {dashboard?.emptyMessage && !loading && (summary.events || 0) === 0 ? (
            <div className="dashboard-empty" role="status">
              <FiCalendar aria-hidden="true" />
              <div>
                <strong>Seu painel está pronto para o próximo evento.</strong>
                <span>{dashboard.emptyMessage}</span>
              </div>
            </div>
          ) : null}
        </div>
      </main>
    </div>
  );
}

function DashboardCard({ title, value, detail, tone = "blue", Icon = FiDatabase }) {
  return (
    <article className={`card-dashboard ${tone}`}>
      <div className="card-dashboard-heading">
        <span className="card-title">{title}</span>
        <span className="card-dashboard-icon" aria-hidden="true">
          <Icon />
        </span>
      </div>
      <strong className="card-value">{formatMetric(value)}</strong>
      <span className="card-detail">{detail}</span>
    </article>
  );
}

function PanelHeading({ Icon, kicker, title, description, titleId }) {
  return (
    <div className="dashboard-panel-title">
      <span className="dashboard-panel-icon" aria-hidden="true">
        <Icon />
      </span>
      <div>
        <span className="dashboard-panel-kicker">{kicker}</span>
        <h2 id={titleId}>{title}</h2>
        <p>{description}</p>
      </div>
    </div>
  );
}

function OperationalFocusPanel({ cards }) {
  return (
    <section className="dashboard-panel dashboard-focus-panel" aria-labelledby="dashboard-focus-title">
      <div className="dashboard-panel-header">
        <PanelHeading
          Icon={FiActivity}
          kicker="Sinal operacional"
          title="O que pede atenção"
          description=""
          titleId="dashboard-focus-title"
        />
        <Link className="dashboard-panel-link" to={APP_ROUTES.PUBLICATION_OPERATIONS}>
          Abrir gerenciamento
          <FiArrowUpRight aria-hidden="true" />
        </Link>
      </div>

      <div className="dashboard-attention-list">
        {cards.map(({ title, value, tone, icon: Icon, detail }) => (
          <div key={title} className={`dashboard-attention-item ${tone}`}>
            <span className="dashboard-attention-icon" aria-hidden="true">
              <Icon />
            </span>
            <div className="dashboard-attention-copy">
              <strong>{formatMetric(value)}</strong>
              <span>{title}</span>
              <small>{detail}</small>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function DashboardIndicatorCard({ title, value, Icon = FiActivity, tone = "neutral" }) {
  return (
    <article className={`dashboard-indicator-card ${tone}`}>
      <span className="dashboard-indicator-icon" aria-hidden="true">
        <Icon />
      </span>
      <span className="dashboard-indicator-label">{title}</span>
      <strong>{formatMetric(value)}</strong>
    </article>
  );
}

function RecognitionDashboardPanel({ loading, error, operations }) {
  const health = operations?.health || {};
  const jobs = operations?.jobs || {};
  const reprocessing = operations?.reprocessing || {};
  const healthPresentation = getRecognitionHealthPresentation(health.status);

  return (
    <section className="dashboard-panel recognition-dashboard-panel" aria-labelledby="recognition-dashboard-title">
      <div className="dashboard-panel-header">
        <PanelHeading
          Icon={FiCamera}
          kicker="Pipeline de fotos"
          title="Análise facial"
          description={
            loading
              ? "Atualizando o estado da análise facial..."
              : ""
          }
          titleId="recognition-dashboard-title"
        />
        <Link className="dashboard-panel-link" to={APP_ROUTES.RECOGNITION_OPERATIONS}>
          Ver fila
          <FiArrowUpRight aria-hidden="true" />
        </Link>
      </div>

      {error ? (
        <div className="dashboard-panel-error" role="alert">
          <FiAlertCircle aria-hidden="true" />
          <span>{error}</span>
        </div>
      ) : null}

      {!loading && !error ? (
        <>
          <div className={`recognition-dashboard-health ${healthPresentation.tone}`}>
            <span className="recognition-health-dot" aria-hidden="true" />
            <div>
              <span>Estado atual</span>
              <strong>{healthPresentation.label}</strong>
            </div>
          </div>

          <div className="dashboard-indicator-grid">
            <DashboardIndicatorCard title="Aguardando Reconhecimento" value={jobs.pending || 0} tone="warning" Icon={FiClock} />
            <DashboardIndicatorCard title="Processando Reconhecimento" value={jobs.processing || 0} tone="info" Icon={FiRefreshCw} />
            <DashboardIndicatorCard title="Falha no Reconhecimento" value={jobs.failed || 0} tone="danger" Icon={FiAlertCircle} />
            <DashboardIndicatorCard title="Ignorado" value={jobs.skipped || 0} tone="warning" Icon={FiAlertTriangle} />
            <DashboardIndicatorCard title="Reprocessamentos" value={reprocessing.manualCountAvailable ? reprocessing.manualCount || 0 : "-"} tone="info" Icon={FiRefreshCw} />
          </div>
        </>
      ) : null}
    </section>
  );
}

function DashboardQuickActions({ actions }) {
  return (
    <section className="dashboard-panel dashboard-actions-panel" aria-labelledby="dashboard-actions-title">
      <div className="dashboard-panel-header">
        <PanelHeading
          Icon={FiArrowUpRight}
          kicker="Próximo passo"
          title="Ações rápidas"
          description=""
          titleId="dashboard-actions-title"
        />
      </div>

      <div className="dashboard-action-list">
        {actions.map(({ label, description, path, icon: Icon }) => (
          <Link key={label} to={path} className="dashboard-action-link">
            <span className="dashboard-action-icon" aria-hidden="true">
              <Icon />
            </span>
            <span className="dashboard-action-copy">
              <strong>{label}</strong>
              <small>{description}</small>
            </span>
            <FiChevronRight className="dashboard-action-chevron" aria-hidden="true" />
          </Link>
        ))}
      </div>
    </section>
  );
}

export default Menu;
